/**
 * Axios middleware script file
 *
 * @package    Middleware
 * @author     Vadym Karpenko <vkarpenko@mindk.com>
 * @copyright  Copyright (c) 2010-2018 MindK (http://mindk.com). All rights reserved.
 * @license    http://mindk.com
 * @link       http://mindk.com
 */

import { create } from 'apisauce';
import { replace, push } from 'react-router-redux';
import { toastr } from 'react-redux-toastr';
import { SubmissionError } from 'redux-form';

import { unAuthorize } from 'Actions/auth';

/**
 * Axios middleware
 *
 * @param interceptors
 *
 * @returns {function(*=)}
 */
export default interceptors => store => {
    const axiosInstance = () => {
        const state = store.getState();
        const headers = {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'Language-Code': state.translation.locale,
        };

        if (state.auth.AccessToken) {
            headers['Authorization'] = `${state.auth.accessTokenType} ${state.auth.AccessToken}`;
        }

        const instance = create({
            baseURL: state.general.apiHost, // eslint-disable-line
            headers,
        });
        instance.addRequestTransform(request => {
            if (request.headers && request.headers['Content-Type'] && request.headers['Content-Type'] === 'multipart/form-data') {
                let formData = new FormData();
                Object.keys(request.data).forEach(name => {
                    if (Array.isArray(request.data[name])) {
                        request.data[name].forEach(item => {
                            formData.append(`${name}[]`, item);
                        });
                    } else {
                        if (request.data[name] !== null) {
                            formData.append(name, request.data[name]);
                        }
                    }
                });

                request.data = formData;
            } else if (state.auth.user && state.auth.user.activeRole && (!request.headers['content-type'] || request.headers['content-type'] !== 'multipart/form-data')) {
                request.data = {
                    ...request.data,
                    active_role_slug: state.auth.user.activeRole.role
                };
            }
        });
        instance.addResponseTransform(response => {
            response.data = response.data || {};

            if (!response.ok) {
                if (response.status === 401 && state.auth.isLoggedIn) {
                    store.dispatch(unAuthorize());
                }
                if (response.status === 403) {
                    store.dispatch(replace('/403'));
                }
                if (response.status === 404) {
                    store.dispatch(replace('/404'));
                }
                if (~[400, 500].indexOf(response.status)) {
                    store.dispatch(push('/500'));
                }

                response.data.status = response.status;

                throw response;
            }
        });
        if (interceptors) {
            interceptors.map(i => i(instance));
        }

        return instance;
    };

    return next => action => {
        if (action.request && !action.type) {
            return apiCall(action)(next, axiosInstance(), store.dispatch, store.getState);
        }

        const result = next(action);
        if (typeof result === 'function') {
            return result(axiosInstance());
        }

        return result;
    };
};

/**
 * Default callback for process response
 *
 * @param {Object} response
 *
 * @returns {{payload: any | {} | *}}
 */
const defaultCallback = response => {
    return {
        payload: {
            data: response.data || response,
            metadata: response.data
                ? {
                    lastPage: response.lastPage,
                    page: response.page,
                    perPage: response.perPage,
                    total: response.total
                }
                : {}
        }
    };
};


/**
 /**
 * Create API call action
 *
 * @param {String} requestType
 * @param {function} request
 * @param {function} success function
 * @param {function} error function
 *
 * @return {function}
 */
const apiCall = ({ requestType, request, success = defaultCallback, error = defaultCallback }) => (next, axiosInstance, dispatch, getState) => {
    next({
        type: `${requestType}_REQUEST`,
    });

    return request(axiosInstance).then((response) => {
        try {
            const payload = success(response.data, dispatch, getState);
            if (response.data && response.data.message) {
                toastr.success(response.data.message, { timeOut: 20000 });
            }
            if (payload) {
                return next({
                    type: `${requestType}_SUCCESS`,
                    ...payload,
                });
            }
        } catch (e) {
            return next({
                type: `${requestType}_ERROR`,
                payload: {},
            });
        }
    }).catch((response) => {
        const payload = error(response.data, dispatch, getState);
        if (response.data && response.data.message) {
            toastr.error(response.data.message, { timeOut: 20000 });
        }
        if (payload) {
            next({
                type: `${requestType}_ERROR`,
                ...payload,
            });
            if (response.status === 422 && response.data.details) {
                throw new SubmissionError(response.data.details);
            }
        }
    });
};



// WEBPACK FOOTER //
// ./src/middleware/axios.js